import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { closeAllPopupsIfRequired } from '../../../components/popupPanel/popupActions';
import { activePopupSelector } from '../../../components/popupPanel/popupOpenSelector';
import { selectSelectedElementChip } from '../../../element/chipsPanel/elementChipsSelectors';
import { isCurrentlyEditing } from '../../../element/selection/currentlyEditingSelector';
import { getSelectedElementIds } from '../../../element/selection/selectedElementsSelector';
import { isDraggingSelector } from '../../../reducers/draggingSelector';
import Mousetrap from '../../shortcuts/Mousetrap';
import type { CurrentToolbarReducerState } from '../currentToolbarReducer';
import { selectCurrentToolbarState } from '../selector/currentToolbarSelector';
import type { ToolbarList } from '../toolbarTypes';

type ToolbarPopoverContext = {
    toolbarKey: string | null;
    selectedElementChipId: string | null;
    isEditing: boolean;
};

/** Returns the active toolbar list when the toolbar state still has one. */
const getActiveToolbar = (toolbarState: CurrentToolbarReducerState): ToolbarList | null => {
    if (!toolbarState) return null;

    return toolbarState.toolbars[toolbarState.focusIndex] ?? null;
};

/** Returns the stable identity of the active toolbar target. */
export const getToolbarKey = (toolbarState: CurrentToolbarReducerState): string | null => {
    const activeToolbar = getActiveToolbar(toolbarState);
    if (!activeToolbar) return null;

    const elementTypes = [...activeToolbar.elementTypes].sort().join(',');
    return `${activeToolbar.type}|${elementTypes}|${activeToolbar.id}`;
};

/** Coordinates a Roma popover with the toolbar context, legacy popups, and workspace shortcuts. */
const useToolbarPopover = () => {
    const dispatch = useDispatch();
    const activePopups = useSelector(activePopupSelector);
    const currentSelectedElementIds = useSelector(getSelectedElementIds);
    const isEditing = useSelector(isCurrentlyEditing);
    const isDragging = useSelector(isDraggingSelector);
    const selectedElementChip = useSelector(selectSelectedElementChip);
    const toolbarState = useSelector(selectCurrentToolbarState);
    const triggerPressStartedOpenRef = useRef(false);
    const [openContext, setOpenContext] = useState<ToolbarPopoverContext | null>(null);
    const isOpen = openContext !== null;

    const onOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                dispatch(closeAllPopupsIfRequired());
                setOpenContext({
                    toolbarKey: getToolbarKey(toolbarState),
                    selectedElementChipId: selectedElementChip?.chip.id ?? null,
                    isEditing,
                });
                return;
            }

            setOpenContext(null);
        },
        [dispatch, isEditing, selectedElementChip, toolbarState],
    );

    const onTriggerPressStart = useCallback(() => {
        triggerPressStartedOpenRef.current = isOpen;
    }, [isOpen]);

    const onTriggerPress = useCallback(() => {
        if (!triggerPressStartedOpenRef.current) return;

        triggerPressStartedOpenRef.current = false;
        onOpenChange(false);
    }, [onOpenChange]);

    // Pause workspace shortcuts while focus and keyboard input belong to the popover.
    useEffect(() => {
        if (!isOpen) return;

        Mousetrap.pause();
        return () => Mousetrap.resume();
    }, [isOpen]);

    // Dismiss the popover when the toolbar target or interaction context is no longer safe to reuse.
    useEffect(() => {
        if (!openContext) return;

        const activeToolbar = getActiveToolbar(toolbarState);
        const capturedSelectedElementIds = activeToolbar?.capturedState?.selectedElementIds;
        const selectedElementIdsChanged =
            !!capturedSelectedElementIds && !capturedSelectedElementIds.equals(currentSelectedElementIds);
        const selectedElementChipChanged = openContext.selectedElementChipId !== (selectedElementChip?.chip.id ?? null);
        const selectionChanged = selectedElementIdsChanged || selectedElementChipChanged;
        const toolbarContextChanged = openContext.toolbarKey !== getToolbarKey(toolbarState);
        const editingContextChanged = openContext.isEditing !== isEditing;

        if (!activePopups.size && !selectionChanged && !toolbarContextChanged && !editingContextChanged && !isDragging)
            return;

        setOpenContext(null);
    }, [activePopups, currentSelectedElementIds, isDragging, isEditing, openContext, selectedElementChip, toolbarState]);

    return { isOpen, onOpenChange, onTriggerPress, onTriggerPressStart };
};

export default useToolbarPopover;
